// @ts-nocheck
import { useEffect, useRef, useState, Ref, useMemo, useCallback } from "react";
import { useNavigationStore } from "src/store/navigationStore";

import { Canvas, useFrame, RootState } from "@react-three/fiber";
import { Splat, useGLTF, useAnimations } from "@react-three/drei";
import * as THREE from "three";
import { Group } from "three";

import { GLB_AVATAR } from "src/const/navigationConst";

import { useModelViewerStore } from "@/store/modelViewerStore";

import {
  roundToDecimals,
  vec3Distance,
  hexToRgbArray,
  getYAngleDegree,
  angleDifferenceABtoAC,
  wrapAngle,
  calculateTargetPosition,
  normalizeRotation,
} from "src/utils/game";
import * as pc from "playcanvas";
import { OrbitControls } from "@playcanvas/react/scripts";
import {
  useTexture,
  useMaterial,
  useEnvAtlas,
  useApp,
  useSplat,
  useModel,
  useAppEvent,
} from "@playcanvas/react/hooks";
import { Application, Entity, Gltf } from "@playcanvas/react";
import {
  Render,
  Camera,
  Light,
  GSplat,
  Script,
  Environment,
} from "@playcanvas/react/components";
import { CameraControls } from "playcanvas/scripts/esm/camera-controls.mjs";

import WatermarkScreen from "src/components/watermark";
import { useLodSplat } from "src/utils/useLodSplat";
import LottieLoading from "src/components/lottie/lottie-loading";

import { GsplatRevealRadial } from 'src/extra_js/reveal-radial.mjs';

export default function ModelViewer() {
  const setIsModelLoaded = useModelViewerStore(
    (state) => state.setIsModelLoaded,
  );
  const setErrorMessage = useModelViewerStore((state) => state.setErrorMessage);

  const moveSpeedRef = useRef<number>(2);
  const rotateSpeedRef = useRef<number>(20);

  const defaultPositionRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, 0));
  const defaultTargetRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, -1));

  const targetAltitudeRef = useRef<number>(0);

  const latestCurrentLevelNameRef = useRef<string>("");

  const mainObjectEntityRef = useRef();
  const cameraScriptRef = useRef();
  const firstPersonCameraEntityRef = useRef();
  const thirdPersonCameraEntityRef = useRef();
  const latestCameraModeRef = useRef<number>(0);

  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const isMovingRef = useRef<boolean>(false);
  const isRotatingRef = useRef<boolean>(false);

  const latestPositionJoystickData = useRef<number[]>([0, 0]);
  const latestRotationJoystickData = useRef<number[]>([0, 0]);
  const latestRotationScreenData = useRef<number[]>([0, 0]);

  const latestResetPositionRotationEvent = useRef<number>(0);
  const latestResetOnlyRotationEvent = useRef<number>(0);

  const latestVenueModelConfigRef = useRef<VenueModelConfig | null>(null);

  const isInstantUpdate = useRef<boolean>(false);

  const latestModelJsonData = useRef<any>(null);

  useEffect(() => {
    setIsModelLoaded(false);
    setErrorMessage("");
  }, []);

  function useQueryParams() {
    return useMemo(() => new URLSearchParams(window.location.search), []);
  }

  const DataController = () => {
    const resetPositionRotationEvent = useModelViewerStore(
      (state) => state.resetPositionRotationEvent,
    );
    useEffect(() => {
      latestResetPositionRotationEvent.current = resetPositionRotationEvent;
    }, [resetPositionRotationEvent]);

    const resetOnlyRotationEvent = useModelViewerStore(
      (state) => state.resetOnlyRotationEvent,
    );
    useEffect(() => {
      latestResetOnlyRotationEvent.current = resetOnlyRotationEvent;
    }, [resetOnlyRotationEvent]);

    const positionJoystickData = useModelViewerStore(
      (state) => state.positionJoystickData,
    );
    const rotationJoystickData = useModelViewerStore(
      (state) => state.rotationJoystickData,
    );

    useEffect(() => {
      latestPositionJoystickData.current = [
        positionJoystickData[0],
        positionJoystickData[1],
      ];
    }, [positionJoystickData]);

    useEffect(() => {
      latestRotationJoystickData.current = [
        rotationJoystickData[0],
        rotationJoystickData[1],
      ];
    }, [rotationJoystickData]);

    return <></>;
  };

  const MainCamera = () => {
    const app = useApp();
    const cameraRef = useRef();
    let _localProgress;
    let _localCameraMode;
    let isCameraModeUpdated;
    let nearestFixedCameraPosition;

    let finalCameraPosition;
    let finalCameraLookingPosition;

    app.on("update", function (dt) {
      if (cameraScriptRef.current == null) {
        return;
      }

      isCameraModeUpdated = _localCameraMode != latestCameraModeRef.current;
      _localCameraMode = latestCameraModeRef.current;

      if (
        isInstantUpdate.current ||
        isCameraModeUpdated ||
        isMovingRef.current ||
        isRotatingRef.current
      ) {
        const newPosition =
          latestCameraModeRef.current == 0
            ? firstPersonCameraEntityRef.current.getPosition()
            : thirdPersonCameraEntityRef.current.getPosition();
        cameraScriptRef.current.reset(
          mainObjectEntityRef.current.getPosition(),
          newPosition,
        );

        isMovingRef.current = false;
        isRotatingRef.current = false;
        isInstantUpdate.current = false;
      }
    });

    useEffect(() => {
      const defaultCameraLookingPosition = new pc.Vec3(0, 0, 0);
      const defaultPosition = new pc.Vec3(0, 0, 0);
      cameraScriptRef.current.reset(
        defaultCameraLookingPosition,
        defaultPosition,
      );
    }, []);

    app.on("update", function (dt) {
      if (cameraScriptRef.current == null) {
        return;
      }
    });

    return (
      <Entity>
        <Camera fov={60} />
        <Script
          ref={cameraScriptRef}
          script={CameraControls}
          enableFly={false}
        />
      </Entity>
    );
  };

  const MainObject = () => {
    const queryParams = useQueryParams();
    const modelLink = queryParams.get("model");
    const app = useApp();

    const defaultPositionRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, 0));
    const defaultTargetRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, -1));

    let goDownAnimationTime = 0;
    const goDownDuration = 3;

    let goUpAnimationTime = 0;
    const goUpDuration = 2;

    const eulerRef = useRef(new pc.Vec3(0, 0, 0));

    let _localResetPositionRotationEvent = 0;
    let _localResetOnlyRotationEvent = 0;
    let _localLeaveVenueEvent = 0;
    let temp = false;

    let tempY;
    let tempX;
    let tempZ;

    let isInit;
    let processingX;
    let processingY;
    let processingZ;

    useEffect(() => {
      if (latestModelJsonData.current == null) {
        if (!modelLink) {
          return;
        }
        const modelJsonLink = modelLink.replace(".sog", ".json");
        fetchModelJsonData(modelJsonLink);
      } else {
        // setInitPositionAngle(
        //   latestModelJsonData.current.tx,
        //   latestModelJsonData.current.ty,
        //   latestModelJsonData.current.tz,
        //   latestModelJsonData.current.qx,
        //   latestModelJsonData.current.qy,
        //   latestModelJsonData.current.qz,
        //   latestModelJsonData.current.qw,
        // );
        if (
          latestModelJsonData.current.position &&
          latestModelJsonData.current.target
        ) {
          setInitPositionTarget(
            latestModelJsonData.current.position.x,
            latestModelJsonData.current.position.y,
            latestModelJsonData.current.position.z,
            latestModelJsonData.current.target.x,
            latestModelJsonData.current.target.y,
            latestModelJsonData.current.target.z,
          );
        }
      }
    }, []);

    const fetchModelJsonData = async (link: string) => {
      try {
        const response = await fetch(link);
        if (!response.ok) throw new Error("Model Json Fetch failed");
        const jsonData = await response.json();
        latestModelJsonData.current = jsonData;

        if (jsonData.position && jsonData.target) {
          console.log("!!!!!!");
          console.log(jsonData);
          setInitPositionTarget(
            jsonData.position.x,
            jsonData.position.y,
            jsonData.position.z,
            jsonData.target.x,
            jsonData.target.z,
            jsonData.target.z,
          );
        } else {
          setInitPositionTarget(0, 0, 0, 0, 0, 1);
        }
        // setInitPositionAngle(
        //   jsonData.tx,
        //   jsonData.ty,
        //   jsonData.tz,
        //   jsonData.qx,
        //   jsonData.qy,
        //   jsonData.qz,
        //   jsonData.qw,
        // );
      } catch (err) {
        console.log("set 0,0,0 position");
        //setInitPositionAngle(0, 0, 0, 0, 0, 0, 0);
        setInitPositionTarget(0, 0, 0, 0, 0, 1);
      } finally {
      }
    };

    const setInitPositionTarget = (
      positionX: number,
      positionY: number,
      positionZ: number,
      targetX: number,
      targetY: number,
      targetZ: number,
    ) => {
      defaultPositionRef.current.set(-positionX, positionY, -positionZ);
      defaultTargetRef.current.set(-targetX, positionY, -targetZ);

      // defaultPositionRef.current.set(-0.474536037504079,-0.7370345504892006,5.680311502600893);
      // defaultTargetRef.current.set(8.18729842222322,-2.6191227007002995,1.0510169573039843);

      mainObjectEntityRef.current.setPosition(defaultPositionRef.current);
      mainObjectEntityRef.current.lookAt(defaultTargetRef.current);
      const initAngles = mainObjectEntityRef.current.getEulerAngles();

      if (initAngles.z < 90 && initAngles.z > -90) {
        console.log("type A");
        eulerRef.current.set(initAngles.x, initAngles.y, 0);
      } else if (initAngles.z > 90 || initAngles.z < -90) {
        console.log("type B");
        eulerRef.current.set(initAngles.x - 180, 180 - initAngles.y, 0);
      } else {
        console.log("type C");
        eulerRef.current.set(0, 0, 0);
      }
      isInstantUpdate.current = true;
    };

    const setInitPositionAngle = (
      tx: number,
      ty: number,
      tz: number,
      qx: number,
      qy: number,
      qz: number,
      qw: number,
    ) => {
      defaultPositionRef.current.set(-tx, -ty, -tz);
      const target = calculateTargetPosition(-tx, -ty, -tz, -qx, qy, qz, qw);
      defaultTargetRef.current.set(target.x, target.y, target.z);
      mainObjectEntityRef.current.setPosition(defaultPositionRef.current);
      mainObjectEntityRef.current.lookAt(defaultTargetRef.current);
      const initAngles = mainObjectEntityRef.current.getEulerAngles();

      if (initAngles.z < 90 && initAngles.z > -90) {
        console.log("type A");
        eulerRef.current.set(initAngles.x, initAngles.y, 0);
      } else if (initAngles.z > 90 || initAngles.z < -90) {
        console.log("type B");
        eulerRef.current.set(initAngles.x - 180, 180 - initAngles.y, 0);
      } else {
        console.log("type C");
        eulerRef.current.set(0, 0, 0);
      }
      isInstantUpdate.current = true;
    };

    app.on("update", function (dt) {
      if (mainObjectEntityRef.current == null) {
        return;
      }
      let isResetPositionRotation =
        _localResetPositionRotationEvent !=
        latestResetPositionRotationEvent.current;
      _localResetPositionRotationEvent =
        latestResetPositionRotationEvent.current;

      let isResetOnlyRotation =
        _localResetOnlyRotationEvent != latestResetOnlyRotationEvent.current;
      _localResetOnlyRotationEvent = latestResetOnlyRotationEvent.current;

      if (latestPositionJoystickData.current.length != 2) {
        return;
      }

      if (
        latestPositionJoystickData.current[0] != 0 ||
        latestPositionJoystickData.current[1] != 0
      ) {
        isMovingRef.current = true;
        const worldMat = mainObjectEntityRef.current.getWorldTransform();
        const forward = new pc.Vec3(0, 0, -1);
        worldMat.transformVector(forward, forward);
        // forward.y = 0;
        forward.normalize();
        const up = pc.Vec3.UP.clone();
        const right = new pc.Vec3();
        right.cross(up, forward).normalize();
        const forwardScale =
          latestPositionJoystickData.current[1] * moveSpeedRef.current * -dt;
        const rightScale =
          latestPositionJoystickData.current[0] * moveSpeedRef.current * -dt;
        const forwardMove = forward.clone().mulScalar(forwardScale);
        const rightMove = right.clone().mulScalar(rightScale);
        const move = new pc.Vec3();
        move.add(forwardMove).add(rightMove);
        mainObjectEntityRef.current.translate(move);
      }

      if (
        latestRotationJoystickData.current[0] != 0 ||
        latestRotationJoystickData.current[1] != 0
      ) {
        isRotatingRef.current = true;

        eulerRef.current.y +=
          -latestRotationJoystickData.current[0] * rotateSpeedRef.current * dt; // Yaw
        eulerRef.current.x +=
          -latestRotationJoystickData.current[1] * rotateSpeedRef.current * dt; // Pitch

        eulerRef.current.x = pc.math.clamp(eulerRef.current.x, -45, 45);
        eulerRef.current.y = wrapAngle(eulerRef.current.y);
        mainObjectEntityRef.current.setLocalEulerAngles(eulerRef.current);
      }

      if (
        latestRotationScreenData.current[0] != 0 ||
        latestRotationScreenData.current[1] != 0
      ) {
        isRotatingRef.current = true;
        eulerRef.current.y +=
          -latestRotationScreenData.current[0] * rotateSpeedRef.current * dt; // Yaw
        eulerRef.current.x +=
          -latestRotationScreenData.current[1] * rotateSpeedRef.current * dt; // Pitch

        eulerRef.current.x = pc.math.clamp(eulerRef.current.x, -45, 45);
        eulerRef.current.y = wrapAngle(eulerRef.current.y);

        mainObjectEntityRef.current.setLocalEulerAngles(eulerRef.current);
      }
    });

    return (
      <Entity ref={mainObjectEntityRef}>
        {/*<Avatar />*/}
        <Entity
          ref={firstPersonCameraEntityRef}
          position={[0, 0, 0.001]}
        ></Entity>
        <Entity
          ref={thirdPersonCameraEntityRef}
          position={[0, 0.5, 4]}
        ></Entity>
      </Entity>
    );
  };

  const ModelPly = () => {
    const queryParams = useQueryParams();
    const model = queryParams.get("model");
    const modelLink = queryParams.get("model");
    const app = useApp();
    const gsplatRef = useRef();
    const { asset, error } = useSplat(model);

    const createRadialScript = () => {
        const startPosition = firstPersonCameraEntityRef.current.getPosition();
        const script = gsplatRef.current.script?.create(GsplatRevealRadial);
        if (script) {
            script.center.set(startPosition.x, startPosition.y, startPosition.z);
            script.speed = 10;
            script.acceleration = 0;
            script.delay = 1.2;
            script.dotTint.set(0, 1, 1); // Cyan
            script.waveTint.set(0, 1, 0); // Orange
            script.oscillationIntensity = 1;
            script.endRadius = 10000;
        }
        return script;
    };

    if (error) {
      console.error("Splat load error:", error);
      setErrorMessage(error);
    }

    useEffect(() => {
      if (!asset) {
        return;
      }
      const gsplatEntity = gsplatRef.current;
      if (!gsplatRef.current || !app) return;
      gsplatRef.current.gsplat.enabled = true;
      gsplatRef.current.gsplat.unified = true;
      gsplatRef.current.gsplat.asset = asset;
      setIsModelLoaded(true);

      if (latestModelJsonData.current == null) {
        const modelJsonLink = modelLink.replace(".sog", ".json");
        fetchModelJsonData(modelJsonLink);
      } else {
        if (!latestModelJsonData.current.rotate) {
          return;
        }
        const rotationData = new pc.Vec3(
          latestModelJsonData.current.rotate.x,
          latestModelJsonData.current.rotate.y,
          latestModelJsonData.current.rotate.z,
        );
        setModelRotation(rotationData);
      }


      gsplatEntity.addComponent('script');
      createRadialScript();

    }, [asset]);

    const fetchModelJsonData = async (link: string) => {
      try {
        const response = await fetch(link);
        if (!response.ok) throw new Error("Model Json Fetch failed");
        const jsonData = await response.json();
        latestModelJsonData.current = jsonData;
        if (!jsonData.rotate) {
          return;
        }
        const rotationData = new pc.Vec3(
          jsonData.rotate.x,
          jsonData.rotate.y,
          jsonData.rotate.z,
        );
        setModelRotation(rotationData);
      } catch (err) {
      } finally {
      }
    };

    //TEMP only for demo ,  use superspl rotation
    const setModelRotation = (rotation: pc.Vec3) => {
      gsplatRef.current.setEulerAngles(
        rotation.x + 180,
        rotation.y,
        rotation.z + 180,
      );
    };

    return (
      <Entity
        ref={gsplatRef}
        rotation={[180, 0, 0]}
        position={[0, 0, 0]}
        scale={[1 / 1 / 1]}
      >
        <GSplat />
      </Entity>
    );
  };

  const PlaycanvasScene = () => {
    const app = useApp();
    return (
      <>
        <ModelPly />
        <MainObject />
        <MainCamera />
      </>
    );
  };

  const Scene = () => {
    return (
      <Application>
        <PlaycanvasScene />
      </Application>
    );
  };

  const Loading = () => {
    const isModelLoaded = useModelViewerStore((state) => state.isModelLoaded);
    return !isModelLoaded ? (
      <div className="frosted-fullscreen flex flex-col items-center justify-center">
        <LottieLoading />
      </div>
    ) : (
      <></>
    );
  };

  const ErrorMessage = () => {
    const errorMessage = useModelViewerStore((state) => state.errorMessage);
    return errorMessage != "" ? (
      <div className="frosted-fullscreen flex flex-col items-center justify-center">
        {errorMessage}
      </div>
    ) : (
      <></>
    );
  };

  console.log(
    "Model Viewer Page component rendered , if this keep showing, something wrong",
  );

  const DragController = () => {
    const isDraggingRef = useRef(false);
    const startPosRef = useRef({ x: 0, y: 0 });
    const previousPositionRef = useRef({ x: 0, y: 0 });
    const getClientPos = (e) => {
      if (e.touches && e.touches.length > 0) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      return { x: e.clientX, y: e.clientY };
    };
    const handleStart = useCallback((e) => {
      const pos = getClientPos(e);
      isDraggingRef.current = true;
      previousPositionRef.current = { x: pos.x, y: pos.y };
    }, []);
    const handleMove = useCallback((e) => {
      if (!isDraggingRef.current) {
        return;
      }
      const pos = getClientPos(e);
      const xChange = pos.x - previousPositionRef.current.x;
      const yChange = pos.y - previousPositionRef.current.y;
      previousPositionRef.current = { x: pos.x, y: pos.y };
      latestRotationScreenData.current[0] = xChange * 0.5;
      latestRotationScreenData.current[1] = yChange * 0.5;
    }, []);
    const handleEnd = useCallback(() => {
      isDraggingRef.current = false;
      latestRotationScreenData.current[0] = 0;
      latestRotationScreenData.current[1] = 0;
    }, []);
    useEffect(() => {
      window.addEventListener("mouseup", handleEnd);
      window.addEventListener("touchend", handleEnd);
      return () => {
        window.removeEventListener("mouseup", handleEnd);
        window.removeEventListener("touchend", handleEnd);
      };
    }, []);

    return (
      <div
        className="fullscreen_div"
        onMouseDown={handleStart}
        onTouchStart={handleStart}
        onMouseMove={handleMove}
        onTouchMove={handleMove}
        onMouseUp={handleEnd}
        onTouchEnd={handleEnd}
      ></div>
    );
  };

  return (
    <div
      className="absolute top-0 left-0 w-full h-full"
      style={{ backgroundColor: "black" }}
    >
      <Loading />
      <ErrorMessage />
      <DataController />
      <Scene />
      <DragController />
      <WatermarkScreen />
    </div>
  );
}
